import type { FragmentType } from '@/generated/gql/fragment-masking'
import { useFragment } from '@/generated/gql/fragment-masking'
import { graphql } from '@/generated/gql/gql'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import { Divider, IconButton, Stack, Typography } from '@mui/material'

import { useTranslation } from 'react-i18next'

import LocationSelectionGraphql from '../../LocationSelectionGraphql'
import { OrderCutOff } from '../../MenuHeaderV2/OrderCutOff'
import { TimeslotSelectionGraphql } from '../../TimeslotSelectionGraphql'
import CurrentCredits from '../CurrentCredits'

const canteenCartHeaderUserDataFragment = graphql(`
  fragment CanteenCartHeader_UserDataFragment on User {
    accountLocation {
      timezone
    }
    ...CanteenLocationSelection_UserDataFragment
    ...CanteenCartCurrentCredits_UserDataFragment
    account {
      userInterfaceSettings {
        hideCCPrices
      }
    }
  }
`)

interface HeaderProps {
  arrivalTime: string
  availableSubsidy: number
  timeslots: { id: string; startTime: string; endTime: string }[]
  userData: FragmentType<typeof canteenCartHeaderUserDataFragment>
  onClose: () => void
  onChangeTimeslot: (timeslot: string) => void
  orderTimeRemaining: number
}

export function Header({
  onClose,
  onChangeTimeslot,
  arrivalTime,
  availableSubsidy,
  timeslots,
  userData: userDataProp,
  orderTimeRemaining,
}: HeaderProps) {
  const userData = useFragment(canteenCartHeaderUserDataFragment, userDataProp)
  const { t } = useTranslation()

  return (
    <Stack spacing={1.5} marginTop={2}>
      <Stack
        direction="row"
        spacing={1}
        paddingX={3}
        alignItems="center"
        justifyContent="space-between">
        <Typography variant="subtitle2">{t('cc:yourOrder')}</Typography>
        <IconButton onClick={onClose} data-cy="order-modal-close-button">
          <HighlightOffIcon />
        </IconButton>
      </Stack>
      <Divider />
      <Stack direction="row" paddingY={1} paddingX={3} spacing={1} alignItems="center">
        <Typography variant="body2">{t('cc:orderCutOff')}</Typography>
        <OrderCutOff showLabel={false} orderTimeRemaining={orderTimeRemaining} />
      </Stack>
      <Divider />
      <Stack direction="column" paddingX={3} spacing={1}>
        <Typography variant="body1">{t('cc:delivery')}</Typography>
        <LocationSelectionGraphql userData={userData} disabled />
        <TimeslotSelectionGraphql
          timezone={userData.accountLocation?.timezone}
          selectedTimeslot={arrivalTime}
          timeslots={timeslots}
          onChangeTimeslot={onChangeTimeslot}
          disabled
        />
      </Stack>
      <Divider />
    </Stack>
  )
}
